<script lang="ts">
  import { onMount } from 'svelte';
  import { game } from '$stores/game.svelte';
  import { scoreboard } from '$stores/scoreboard.svelte';
  import { tierFor } from '$game/sync';
  import Mascot from './Mascot.svelte';

  let displayPct = $state(0);
  let xpFillReady = $state(false);
  let badgesReady = $state(false);
  let mascotState = $state<'celebrate' | 'wink' | 'idle'>('celebrate');

  // ---- Branching ---------------------------------------------------------
  // Three render branches in this component, all driven from the store:
  //   1. Bonded     — animated twin rate vs. friend's decrypted ranking.
  //   2. Solo echo  — animated echo rate vs. user's previous saved ranking.
  //   3. Solo base  — first play of this content; no number, "BASELINE LOCKED".
  const isBonded = game.mode === 'bonded';
  const isBaseline = game.mode === 'solo' && game.echoBaseline;
  const isEcho = game.mode === 'solo' && !game.echoBaseline;

  const animatedTarget = isBaseline ? 0 : game.syncPct;
  const tier = isBonded
    ? tierFor(game.syncPct)
    : isEcho
      ? game.echoTierLabel
      : '';
  const pctLabel = isBonded ? 'TWIN RATE' : isEcho ? 'ECHO RATE' : 'BASELINE';
  const banner = isBonded
    ? '★ TWIN UNLOCKED ★'
    : isEcho
      ? '✦ ECHO RESOLVED ✦'
      : '◆ BASELINE LOCKED ◆';

  /** Width of the XP fill for the level bar (percent). */
  const levelFillPct = $derived.by(() => {
    const span = Math.max(1, game.xpForNext - game.xpForLevel);
    const filled = Math.max(0, game.xp - game.xpForLevel);
    return Math.min(100, (filled / span) * 100);
  });

  onMount(() => {
    if (isBaseline) {
      // No number to count up to — let the page settle, then enable XP/badges.
      setTimeout(() => {
        xpFillReady = true;
        setTimeout(() => (badgesReady = true), 500);
      }, 600);
      setTimeout(() => (mascotState = 'idle'), 700);
    } else {
      const duration = 1400;
      const start = performance.now() + 200; // 200ms intro stagger
      const tick = (now: number) => {
        if (now < start) {
          requestAnimationFrame(tick);
          return;
        }
        const t = Math.min(1, (now - start) / duration);
        const eased =
          t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; // cubic-bezier(0.2,0.8,0.2,1) approx
        displayPct = Math.round(animatedTarget * eased);
        if (t < 1) requestAnimationFrame(tick);
        else {
          xpFillReady = true;
          setTimeout(() => (badgesReady = true), 500);
        }
      };
      requestAnimationFrame(tick);

      // Wind the mascot down to idle (with a wink for high syncPct).
      setTimeout(() => {
        mascotState = animatedTarget >= 90 ? 'wink' : 'idle';
      }, 700);
    }

    // Scoreboard publish is BONDED-ONLY. Solo echo is a private metric — we
    // never expose vs.-yourself percentages on the public board.
    if (
      isBonded &&
      game.pulse &&
      game.userHandle &&
      game.friendHandle
    ) {
      void scoreboard.publishResult({
        handleA: game.userHandle,
        handleB: game.friendHandle,
        pct: game.syncPct,
        category: game.pulse.categoryLabel,
        pulseId: game.pulse.id
      });
    }
  });
</script>

{#if game.pulse}
  <section class="reveal-stage">
    <div class="reveal-banner">{banner}</div>

    <div class="mascot-slot">
      <Mascot size={70} state={mascotState} />
    </div>

    <div class="pct-stage" class:baseline={isBaseline}>
      <div class="pct-burst"></div>
      <span class="star-l">✦</span>
      <span class="star-r">✦</span>
      {#if isBaseline}
        <div class="pct-game placeholder">—</div>
      {:else}
        <div class="pct-game">{displayPct}%</div>
      {/if}
    </div>

    <div class="pct-label">{pctLabel}</div>
    {#if tier}
      <div class="tier-pill">{tier}</div>
    {/if}
    {#if isBaseline}
      <div class="baseline-copy">
        Replay this category to see how you evolve.
      </div>
    {:else if isEcho}
      <div class="baseline-copy subtle">
        Compared against your last play of this content.
      </div>
    {/if}

    <div class="xp-strip">
      <div class="xp-row">
        <div class="xp-label">XP THIS PULSE</div>
        <div class="xp-gain">+ {game.xpThisPulse} XP</div>
      </div>
      <div class="xp-track">
        <div class="xp-fill" style:width="{xpFillReady ? levelFillPct : 0}%"></div>
      </div>
      <div class="xp-row sm">
        <div class="xp-label">LVL {game.level} · {game.levelTitle}</div>
        <div class="xp-num">{game.xp} / {game.xpForNext}</div>
      </div>
    </div>

    {#if badgesReady && game.earnedBadges.length > 0}
      <div class="badges">
        {#each game.earnedBadges as b (b.id)}
          <div class="badge {b.color}">
            <span class="badge-icon">{b.icon}</span>
            {b.label}
          </div>
        {/each}
      </div>
    {/if}

    <button class="btn btn-hero reveal-cta" onclick={() => game.goto('share')}>
      See your mandala →
    </button>
  </section>
{/if}

<style>
  .reveal-stage {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px var(--gutter) max(var(--safe-cta-bottom), 14px);
    position: relative;
    overflow: hidden;
    gap: 10px;
  }

  /* Confetti dot field overlay specific to the reveal. */
  .reveal-stage::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      radial-gradient(circle at 10% 20%, var(--gold) 1px, transparent 2px),
      radial-gradient(circle at 90% 30%, var(--hot) 1px, transparent 2px),
      radial-gradient(circle at 20% 60%, var(--cyan) 1px, transparent 2px),
      radial-gradient(circle at 85% 75%, var(--gold) 1px, transparent 2px),
      radial-gradient(circle at 50% 10%, #fff 1px, transparent 2px),
      radial-gradient(circle at 40% 85%, var(--hot) 1px, transparent 2px);
    pointer-events: none;
    opacity: 0.7;
    animation: confettiDrift 8s ease-in-out infinite;
  }
  .reveal-stage > * {
    position: relative;
    z-index: 1;
  }
  :global(body.brutal) .reveal-stage::before {
    display: none;
  }
  @keyframes confettiDrift {
    0%, 100% {
      opacity: 0.6;
      transform: translate3d(0, 0, 0);
    }
    50% {
      opacity: 0.9;
      transform: translate3d(2px, -3px, 0);
    }
  }

  .reveal-banner {
    background: var(--gold);
    color: var(--text-on-light);
    font-size: 11px;
    font-weight: 900;
    padding: 5px 16px;
    border-radius: var(--r-pill);
    letter-spacing: 0.2em;
    box-shadow: var(--depth-sm) var(--gold-deep), var(--glow-gold);
    margin: 4px 0;
    transform: rotate(-3deg);
    animation: bannerDrop 600ms var(--spring-bouncy) backwards;
  }
  :global(body.brutal) .reveal-banner {
    background: var(--text);
    color: var(--bg);
    box-shadow: 3px 3px 0 0 var(--text);
  }
  @keyframes bannerDrop {
    0% {
      transform: rotate(-3deg) translateY(-30px);
      opacity: 0;
    }
    60% {
      transform: rotate(-6deg) translateY(4px);
      opacity: 1;
    }
    100% {
      transform: rotate(-3deg) translateY(0);
      opacity: 1;
    }
  }

  .mascot-slot {
    margin: 4px 0;
  }

  .pct-stage {
    position: relative;
    margin: 4px 0;
    isolation: isolate;
    max-width: 100%;
  }
  .pct-burst {
    position: absolute;
    inset: clamp(-20px, -4vw, -10px);
    background: radial-gradient(circle at center, rgba(255, 216, 74, 0.55), transparent 50%);
    pointer-events: none;
    animation: burstScale 1.4s var(--spring-bouncy);
  }
  :global(body.brutal) .pct-burst {
    display: none;
  }
  @keyframes burstScale {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    60% {
      transform: scale(1.1);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 0.8;
    }
  }
  .pct-game {
    font-size: clamp(72px, 22vw, 104px);
    font-weight: 900;
    letter-spacing: -0.05em;
    line-height: 1;
    background: linear-gradient(180deg, var(--gold) 0%, var(--hot) 60%, var(--cyan) 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    text-shadow: 0 0 40px rgba(255, 56, 188, 0.4);
    -webkit-text-stroke: 2px rgba(0, 0, 0, 0.25);
    position: relative;
    -webkit-font-smoothing: antialiased;
    text-rendering: geometricPrecision;
    paint-order: stroke fill;
  }
  :global(body.brutal) .pct-game {
    background: none;
    color: var(--text);
    -webkit-text-fill-color: var(--text);
    -webkit-text-stroke: 0;
    text-shadow: none;
  }
  .star-l,
  .star-r {
    position: absolute;
    color: var(--gold);
    font-size: clamp(22px, 6vw, 28px);
    text-shadow: 0 0 14px var(--gold);
    animation: starWobble 2s ease-in-out infinite;
  }
  :global(body.brutal) .star-l,
  :global(body.brutal) .star-r {
    color: var(--text);
    text-shadow: none;
  }
  .star-l {
    top: -6px;
    left: -8px;
    transform: rotate(-15deg) translateX(-30%);
  }
  .star-r {
    top: 0;
    right: -8px;
    transform: rotate(20deg) translateX(30%);
    animation-delay: 0.4s;
  }
  @keyframes starWobble {
    0%, 100% {
      transform: rotate(-15deg) scale(1);
    }
    50% {
      transform: rotate(-5deg) scale(1.15);
    }
  }

  .pct-game.placeholder {
    -webkit-text-fill-color: var(--gold);
    color: var(--gold);
    -webkit-text-stroke: 0;
    text-shadow: 0 0 24px rgba(255, 216, 74, 0.5);
    background: none;
  }
  :global(body.brutal) .pct-game.placeholder {
    color: var(--text);
    text-shadow: none;
  }
  .baseline-copy {
    margin-top: 4px;
    font-size: 12px;
    line-height: 1.4;
    text-align: center;
    color: var(--text-2);
    max-width: 280px;
  }
  .baseline-copy.subtle {
    color: var(--text-3);
    font-size: 11px;
  }

  .pct-label {
    font-size: 11px;
    color: var(--text);
    text-transform: uppercase;
    letter-spacing: 0.2em;
    font-weight: 900;
    margin-top: 4px;
  }
  .tier-pill {
    margin-top: 6px;
    padding: 7px 16px;
    background: linear-gradient(135deg, var(--hot), var(--violet));
    color: var(--text);
    border-radius: var(--r-pill);
    font-weight: 900;
    font-size: 12px;
    letter-spacing: 0.08em;
    border: 2px solid rgba(255, 255, 255, 0.3);
    box-shadow: var(--depth-sm) #6d2380, var(--glow-hot);
    text-transform: uppercase;
  }
  :global(body.brutal) .tier-pill {
    background: var(--bg);
    color: var(--text);
    border-color: var(--text);
    box-shadow: 3px 3px 0 0 var(--text);
  }

  /* ============ XP strip ============ */
  .xp-strip {
    width: 100%;
    background: rgba(0, 0, 0, 0.5);
    border: 2px solid rgba(255, 216, 74, 0.4);
    border-radius: var(--r-md);
    padding: 10px 14px;
    margin-top: 6px;
  }
  :global(body.brutal) .xp-strip {
    background: var(--bg);
    border-color: var(--text);
    box-shadow: 3px 3px 0 0 var(--text);
  }
  .xp-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 11px;
    font-weight: 800;
  }
  .xp-row.sm {
    font-size: 10px;
    margin-top: 6px;
    color: var(--text-2);
  }
  .xp-label {
    color: var(--text-2);
    letter-spacing: 0.05em;
  }
  .xp-gain {
    color: var(--gold);
    text-shadow: 0 0 10px rgba(255, 216, 74, 0.6);
    font-size: 13px;
    font-weight: 900;
  }
  :global(body.brutal) .xp-gain {
    color: var(--text);
    text-shadow: none;
  }
  .xp-track {
    height: 8px;
    background: rgba(0, 0, 0, 0.6);
    border-radius: var(--r-pill);
    border: 1px solid rgba(255, 255, 255, 0.1);
    overflow: hidden;
    margin-top: 6px;
  }
  :global(body.brutal) .xp-track {
    background: var(--bg);
    border-color: var(--text);
  }
  .xp-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--gold), var(--hot));
    box-shadow: 0 0 10px rgba(255, 216, 74, 0.6);
    border-radius: var(--r-pill);
    transition: width 1.2s var(--spring-bouncy);
  }
  :global(body.brutal) .xp-fill {
    background: var(--text);
    box-shadow: none;
  }
  .xp-num {
    color: var(--gold);
  }
  :global(body.brutal) .xp-num {
    color: var(--text);
  }

  /* ============ Badges ============ */
  .badges {
    display: flex;
    gap: 8px;
    margin-top: 4px;
    width: 100%;
  }
  .badge {
    flex: 1;
    background: rgba(0, 0, 0, 0.5);
    border: 2px solid;
    border-radius: var(--r-md);
    padding: 8px 6px;
    font-size: 10px;
    font-weight: 900;
    text-align: center;
    line-height: 1.2;
    box-shadow: var(--depth-sm) rgba(0, 0, 0, 0.6);
    animation: badgePop 480ms var(--spring-bouncy) backwards;
  }
  :global(body.brutal) .badge {
    background: var(--bg);
    border-color: var(--text);
    color: var(--text);
    box-shadow: 3px 3px 0 0 var(--text);
  }
  .badge:nth-child(1) {
    animation-delay: 0ms;
  }
  .badge:nth-child(2) {
    animation-delay: 180ms;
  }
  .badge:nth-child(3) {
    animation-delay: 360ms;
  }
  .badge.gold {
    border-color: var(--gold);
    color: var(--gold);
  }
  .badge.hot {
    border-color: var(--hot);
    color: var(--hot);
  }
  .badge.cyan {
    border-color: var(--cyan);
    color: var(--cyan);
  }
  .badge-icon {
    font-size: 18px;
    display: block;
    margin-bottom: 3px;
  }
  @keyframes badgePop {
    0% {
      transform: scale(0) rotate(-30deg);
      opacity: 0;
    }
    60% {
      transform: scale(1.2) rotate(8deg);
      opacity: 1;
    }
    100% {
      transform: scale(1) rotate(0);
      opacity: 1;
    }
  }

  /* ============ CTA ============ */
  .reveal-cta {
    margin-top: auto;
    width: 100%;
    text-align: center;
  }
</style>
